import type { ListModule } from '../config/constants';

const STORAGE_PREFIX = 'eminer:new-list-intent:v1';

export interface NewListIntent {
  version: 1;
  requestId: string;
  userId: string;
  module: ListModule;
  currentCount: number;
  requestedAt: string;
}

function storageKey(userId: string, module: ListModule): string {
  return `${STORAGE_PREFIX}:${encodeURIComponent(userId)}:${module}`;
}

function requestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isIntent(value: unknown, userId: string, module: ListModule): value is NewListIntent {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    record['version'] === 1 &&
    typeof record['requestId'] === 'string' &&
    record['requestId'].length > 0 &&
    record['userId'] === userId &&
    record['module'] === module &&
    Number.isInteger(record['currentCount']) &&
    Number(record['currentCount']) >= 0 &&
    typeof record['requestedAt'] === 'string'
  );
}

/**
 * Intenção de domínio durável e isolada por conta/módulo.
 *
 * Ela não é uma lista nem uma fila paralela: existe somente entre a confirmação do botão
 * “Nova lista” e a primeira busca que preparar essa lista no endpoint v2 ou no legado.
 */
export function putNewListIntent(
  userId: string,
  module: ListModule,
  currentCount: number,
): NewListIntent {
  if (!userId) throw new Error('Usuário ausente ao preparar uma nova lista.');
  const intent: NewListIntent = {
    version: 1,
    requestId: requestId(),
    userId,
    module,
    currentCount: Math.max(0, Math.trunc(currentCount)),
    requestedAt: new Date().toISOString(),
  };
  localStorage.setItem(storageKey(userId, module), JSON.stringify(intent));
  return intent;
}

export function getNewListIntent(userId: string, module: ListModule): NewListIntent | null {
  if (!userId) return null;
  const raw = localStorage.getItem(storageKey(userId, module));
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isIntent(parsed, userId, module) ? parsed : null;
  } catch {
    return null;
  }
}

/** Remove somente a intenção que a chamada consumiu; outra aba pode ter criado uma mais nova. */
export function consumeNewListIntent(
  userId: string,
  module: ListModule,
  expectedRequestId?: string,
): boolean {
  const current = getNewListIntent(userId, module);
  if (!current) return false;
  if (expectedRequestId && current.requestId !== expectedRequestId) return false;
  localStorage.removeItem(storageKey(userId, module));
  return true;
}

